import { API_ROUTES } from "lib/constants/api-constants";
import useApi from "./useApi";
import { useState } from "react";

interface PractitionerSubscriptionState {
  // Define the properties of the state here
  loading: boolean;
  error: string | null;
  subscriptions: PractitionerSubscriptionDetails[];
  getSubscriptionsByClinic: (
    clinicId: string,
    onSuccess?: (data: PractitionerSubscriptionDetails[]) => void
  ) => Promise<PractitionerSubscriptionDetails[]>;
  getSubscriptionsByClinicAndRoom: (
    clinicId: string,
    roomId: string,
    onSuccess?: (data: PractitionerSubscriptionDetails[]) => void
  ) => Promise<PractitionerSubscriptionDetails[]>;
  getAllSubscriptions: (
    onSuccess?: (data: PractitionerSubscriptionDetails[]) => void
  ) => Promise<PractitionerSubscriptionDetails[]>;

  updateSubscriptionDates: (
    id: string,
    fromDate: Date,
    selectedDates: string[],
    onSuccess?: (data: PractitionerSubscriptionDetails[]) => void
  ) => Promise<PractitionerSubscriptionDetails[]>;
}

const usePractitionerSubscriptions = (): PractitionerSubscriptionState => {
  const [error, setError] = useState<string | null>();
  const [subscriptions, setSubscriptions] = useState<
    PractitionerSubscriptionDetails[]
  >([]);
  // Using a generic type for the API request

  const {
    getData: getDataRequest,
    loading: getDataRequestLoading,
    putData: putDataRequest,
  } = useApi<PractitionerSubscriptionDetails[]>();

  const onError = (error: string) => {
    setError(error);
  };

  const getSubscriptionsByClinic = async (
    clinicId: string,
    onSuccess?: (data: PractitionerSubscriptionDetails[]) => void
  ): Promise<PractitionerSubscriptionDetails[]> => {
    setError(null); // Reset error state before making the request
    const endPoint = API_ROUTES.PRACTITIONER_SUBSCRIPTIONS.byClinic(clinicId);

    return await getDataRequest(
      endPoint,
      (data) => {
        setSubscriptions(data);
        onSuccess?.(data);
      },
      onError
    );
  };

  const getSubscriptionsByClinicAndRoom = async (
    clinicId: string,
    roomId: string,
    onSuccess?: (data: PractitionerSubscriptionDetails[]) => void
  ): Promise<PractitionerSubscriptionDetails[]> => {
    setError(null); // Reset error state before making the request
    const endPoint = API_ROUTES.PRACTITIONER_SUBSCRIPTIONS.byClinicAndRoom(
      clinicId,
      roomId
    );

    return await getDataRequest(
      endPoint,
      (data) => {
        setSubscriptions(data);
        onSuccess?.(data);
      },
      onError
    );
  };

  const getAllSubscriptions = async (
    onSuccess?: (data: PractitionerSubscriptionDetails[]) => void
  ): Promise<PractitionerSubscriptionDetails[]> => {
    setError(null); // Reset error state before making the request

    return await getDataRequest(
      API_ROUTES.PRACTITIONER_SUBSCRIPTIONS.all,
      (data) => {
        setSubscriptions(data);
        onSuccess?.(data);
      },
      onError
    );
  };

  const updateSubscriptionDates = async (
    id: string,
    fromDate: Date,
    selectedDates: string[],
    onSuccess?: (data: PractitionerSubscriptionDetails[]) => void
  ) => {
    setError(null); // Reset error state before making the request

    return await putDataRequest(
      API_ROUTES.PRACTITIONER_SUBSCRIPTIONS.updateDates(id),
      { fromDate, selectedDates },
      (data) => {
        setSubscriptions(data);
        onSuccess?.(data);
      }
    );
  };

  return {
    loading: getDataRequestLoading,
    error,
    subscriptions: subscriptions,

    getSubscriptionsByClinic,
    getSubscriptionsByClinicAndRoom,
    getAllSubscriptions,
    updateSubscriptionDates,
  };
};

export default usePractitionerSubscriptions;
